import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import * as sessionNotesService from '../../services/sessionNotes';
import { base64Decode } from '../../helpers/base64url';

const ViewSessionNote: React.FC = () => {
  const { encodedData } = useParams<{ encodedData: string }>();
  const navigate = useNavigate();
  const canAccess = useSelector((state: any) => state.auth.permissions?.['session-notes']);
  
  const [sessionNotes, setSessionNotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [sessionDate, setSessionDate] = useState('');
  const [franchiseName, setFranchiseName] = useState('');
  const [centreName, setCentreName] = useState('');
  const [hasDraftNotes, setHasDraftNotes] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedReviews, setSelectedReviews] = useState<any[]>([]);

  useEffect(() => {
    if (encodedData) {
      try {
        const decoded = base64Decode(encodedData);
        const data = JSON.parse(decoded);
        setSessionDate(data.date);
        fetchSessionNotes(data.date, data.franchise_id, data.centre_id);
      } catch (error) {
        toast.error('Invalid session data');
        navigate('/session-notes');
      }
    }
  }, [encodedData]);

  const fetchSessionNotes = async (date: string, franchiseId: number, centreId: number | null) => {
    setLoading(true);
    try {
      const response = await sessionNotesService.getSessionNoteDetails({
        sessionDate: date,
        franchiseId: franchiseId,
        centreId: centreId || undefined,
      });
      if (response?.success && response.collections?.length > 0) {
        setSessionNotes(response.collections);
        // Set franchise and centre names from first record
        if (response.collections[0]) {
          setFranchiseName(response.collections[0].franchise_name);
          setCentreName(response.collections[0].centre_name);
        }
        // Check if any session note has draft status
        const hasDraft = response.collections.some((note: any) => note.status === 'draft');
        setHasDraftNotes(hasDraft);
      } else {
        toast.error('No session notes found');
        navigate('/session-notes');
      }
    } catch (error) {
      toast.error('Failed to fetch session notes');
      navigate('/session-notes');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    if (!status) return null;
    const statusMap: any = {
      draft: { class: 'badge-secondary', text: 'DRAFT' },
      published: { class: 'badge-success', text: 'PUBLISHED' },
      review_pending: { class: 'badge-warning', text: 'REVIEW PENDING' }
    };
    const statusInfo = statusMap[status] || { class: 'badge-secondary', text: status.toUpperCase() };
    return <span className={`badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  const getHomeworkStatusDisplay = (homeworkStatus: string) => {
    if (homeworkStatus === 'completed_and_marked') return 'Completed and Marked';
    if (homeworkStatus === 'completed') return 'Completed';
    if (homeworkStatus === 'not_completed') return 'Not Completed';
    return 'N/A';
  };

  const getHomeworkBadgeClass = (homeworkStatus: string) => {
    if (homeworkStatus === 'completed_and_marked') return 'badge-success';
    if (homeworkStatus === 'completed') return 'badge-info';
    if (homeworkStatus === 'not_completed') return 'badge-danger';
    return 'badge-secondary';
  };

  if (loading) {
    return (
      <div className="content-wrapper">
        <section className="content">
          <div className="container-fluid">
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (sessionNotes.length === 0) {
    return null;
  }

  // Calculate status counts
  const statusCounts = sessionNotes.reduce((acc: any, note: any) => {
    const status = note.status || 'unknown';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const draftCount = statusCounts.draft || 0;
  const reviewPendingCount = statusCounts.review_pending || 0;
  const publishedCount = statusCounts.published || 0;

  return (
    <div className="">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>View Session Note</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <Link to="/">Home</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/session-notes">Session Notes</Link>
                </li>
                <li className="breadcrumb-item active">View</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          {/* Session Details */}
          <div className="card">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center">
                <h3 className="card-title">Session Details</h3>
                <div className="card-tools">
                  {canAccess?.edit && hasDraftNotes && (
                    <Link
                      to={`/session-notes/edit/${encodedData}`}
                      className="btn btn-primary btn-sm mr-2"
                    >
                      <i className="fas fa-edit mr-1"></i>
                      Edit
                    </Link>
                  )}
                  <Link to="/session-notes" className="btn btn-sm btn-default">
                    <i className="fas fa-arrow-left mr-1"></i>
                    Back to List
                  </Link>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <strong>Session Date:</strong>
                  <p>{sessionDate ? moment(sessionDate).format('DD/MM/YYYY') : '--'}</p>
                </div>
                <div className="col-md-4">
                  <strong>Franchise:</strong>
                  <p>{franchiseName || '--'}</p>
                </div>
                <div className="col-md-4">
                  <strong>Centre:</strong>
                  <p>{centreName || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Student Notes */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Student Session Notes</h3>
              <div className="card-tools">
                {draftCount > 0 && (
                  <span className="badge badge-secondary mr-2" title="Draft">
                    Draft: {draftCount}
                  </span>
                )}
                {reviewPendingCount > 0 && (
                  <span className="badge badge-warning mr-2" title="Review Pending">
                    Review: {reviewPendingCount}
                  </span>
                )}
                {publishedCount > 0 && (
                  <span className="badge badge-success" title="Published">
                    Published: {publishedCount}
                  </span>
                )}
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered table-striped" style={{ fontSize: '14px' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>Sr.</th>
                      <th style={{ width: '140px' }}>Student Name</th>
                      <th style={{ width: '90px' }}>Subject</th>
                      <th style={{ width: '80px' }}>Status</th>
                      <th style={{ width: '70px' }}>Attended</th>
                      <th style={{ width: '120px' }}>Homework</th>
                      <th style={{ minWidth: '250px' }}>Session Note</th>
                      <th style={{ width: '250px' }}>Review Comments</th>
                      <th style={{ width: '130px' }}>Author</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessionNotes.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center">
                          No student notes available
                        </td>
                      </tr>
                    ) : (
                      sessionNotes.map((note: any, index: number) => (
                        <tr key={note.id || index}>
                          <td>{index + 1}</td>
                          <td>{note.student_name || '--'}</td>
                          <td>{note.subject_name || '--'}</td>
                          <td>{getStatusBadge(note.status)}</td>
                          <td className="text-center">
                            <span
                              className={`badge ${
                                note.attended === 'yes' ? 'badge-success' : 'badge-danger'
                              }`}
                            >
                              {note.attended === 'yes' ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td className="text-center">
                            {note.attended === 'yes' ? (
                              <span className={`badge ${getHomeworkBadgeClass(note.homework_status)}`}>
                                {getHomeworkStatusDisplay(note.homework_status)}
                              </span>
                            ) : (
                              '--'
                            )}
                          </td>
                          <td>
                            {note.attended === 'yes' ? (
                              <div style={{ 
                                maxHeight: '150px', 
                                overflowY: 'auto', 
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                fontSize: '14px',
                                lineHeight: '1.6',
                                padding: '4px 0'
                              }}>
                                {note.session_note || '--'}
                              </div>
                            ) : '--'}
                          </td>
                          <td style={{ fontSize: '13px' }}>
                            {note.reviews && note.reviews.length > 0 ? (
                              <div>
                                {/* Show latest review */}
                                <div className="mb-1">
                                  <div className="text-muted" style={{ fontSize: '11px', marginBottom: '2px' }}>
                                    <i className="fas fa-comment mr-1"></i>
                                    {note.reviews[note.reviews.length - 1].reviewer_name} - {moment(note.reviews[note.reviews.length - 1].created_at).format('DD/MM/YYYY')}
                                  </div>
                                  <div style={{ 
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    lineHeight: '1.5',
                                    maxHeight: '60px',
                                    overflowY: 'auto'
                                  }}>
                                    {note.reviews[0].review_comments}
                                  </div>
                                </div>
                                {/* Show More button if there are multiple reviews */}
                                {note.reviews.length > 1 && (
                                  <button
                                    className="btn btn-link btn-sm p-0"
                                    style={{ fontSize: '12px' }}
                                    onClick={() => {
                                      setSelectedReviews(note.reviews);
                                      setShowReviewModal(true);
                                    }}
                                  >
                                    <i className="fas fa-eye mr-1"></i>
                                    Show More ({note.reviews.length - 1} older)
                                  </button>
                                )}
                              </div>
                            ) : '--'}
                          </td>
                          <td style={{ fontSize: '13px' }}>
                            {note.created_by_name && note.created_by_name.trim() !== '' && (
                              <div className="mb-2">
                                <div className="text-muted" style={{ fontSize: '11px', marginBottom: '2px' }}>
                                  <i className="fas fa-user mr-1"></i>Created by
                                </div>
                                <div>{note.created_by_name}</div>
                              </div>
                            )}
                            {note.reviewed_by_name && note.reviewed_by_name.trim() !== '' && (
                              <div>
                                <div className="text-muted" style={{ fontSize: '11px', marginBottom: '2px' }}>
                                  <i className="fas fa-user-check mr-1"></i>Reviewed by
                                </div>
                                <div>{note.reviewed_by_name}</div>
                              </div>
                            )}
                            {(!note.created_by_name || note.created_by_name.trim() === '') && 
                             (!note.reviewed_by_name || note.reviewed_by_name.trim() === '') && '--'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Review Comments Modal */}
      {showReviewModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">All Review Comments</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setShowReviewModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {selectedReviews.map((review: any, idx: number) => (
                  <div key={review.id || idx} className="mb-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <strong>{review.reviewer_name}</strong>
                        <div className="text-muted" style={{ fontSize: '13px' }}>
                          <i className="fas fa-calendar mr-1"></i>
                          {moment(review.created_at).format('DD/MM/YYYY HH:mm')}
                        </div>
                      </div>
                    </div>
                    <div style={{ 
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: '1.6',
                      padding: '10px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px'
                    }}>
                      {review.review_comments}
                    </div>
                    {idx < selectedReviews.length - 1 && <hr />}
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowReviewModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewSessionNote;
